import dayjs from 'dayjs'

// Format a single date as its containing Monday–Sunday week range, used in
// tooltip titles since the chart data is on a weekly cadence.
// e.g. 2026-01-08 -> "Jan 05-11, 2026"; 2026-01-30 -> "Jan 26-Feb 1, 2026".
export const formatWeekRange = (isoDate: string): string => {
  const d = dayjs(isoDate)
  const offsetToMonday = (d.day() + 6) % 7
  const monday = d.subtract(offsetToMonday, 'day')
  const sunday = monday.add(6, 'day')
  if (monday.year() === sunday.year() && monday.month() === sunday.month()) {
    return `${monday.format('MMM D')}-${sunday.format('D')}, ${sunday.year()}`
  }
  if (monday.year() === sunday.year()) {
    return `${monday.format('MMM D')}-${sunday.format('MMM D')}, ${sunday.year()}`
  }
  return `${monday.format('MMM D, YYYY')}-${sunday.format('MMM D, YYYY')}`
}

// Calendar-month length each time period should span when the API doesn't
// already cover that range. Using calendar months (rather than a fixed week
// count) keeps the start aligned with the user's mental model — e.g. "6M
// from Apr 16" reaches back to Oct 16, not Oct 23.
const PERIOD_TO_MONTHS: Record<string, number> = {
  '1m': 1,
  '3m': 3,
  '6m': 6,
  '1y': 12
}

// Build a continuous weekly timeline anchored to the latest API date and
// extending back to whichever is further: the start of the selected period or
// the earliest API date. Spanning the selected period guarantees missing
// boundary weeks still render as empty strips on the x-axis; including the
// earliest API date guarantees we never silently drop data the API returned
// (e.g. a 6M response can span 27 weeks — clipping to 26 would hide the
// leading week and suppress its month label).
export const generatePeriodDates = (apiDates: string[], timePeriod: string): string[] => {
  const months = PERIOD_TO_MONTHS[timePeriod.toLowerCase()] ?? 1
  const sorted = [...apiDates].map((d) => dayjs(d).format('YYYY-MM-DD')).sort()
  const end = sorted.length > 0 ? dayjs(sorted[sorted.length - 1]).startOf('day') : dayjs().startOf('day')
  const periodStart = end.subtract(months, 'month')
  const apiStart = sorted.length > 0 ? dayjs(sorted[0]).startOf('day') : end
  const start = periodStart.isBefore(apiStart) ? periodStart : apiStart
  const result: string[] = []
  let cursor = end
  while (cursor.isAfter(start) || cursor.isSame(start, 'day')) {
    result.unshift(cursor.format('YYYY-MM-DD'))
    cursor = cursor.subtract(7, 'day')
  }
  return result
}

// Minimum weekly data points the first month needs to get its own label.
// Below this the month is "partial" — its label sits too close to the Y-axis
// and clips against the 0% tick. The last month is always labeled regardless,
// since its label extends into the right-hand padding rather than colliding
// with axis marks.
const FULL_MONTH_THRESHOLD = 3

export const formatChartXAxisLabels = (dates: string[], timePeriod: string): string[] => {
  const period = timePeriod.toLowerCase()

  if (period === '1m') {
    const formatWeek = (date: string): string => {
      const d = dayjs(date)
      const offsetToMonday = (d.day() + 6) % 7
      const monday = d.subtract(offsetToMonday, 'day')
      const sunday = monday.add(6, 'day')
      if (monday.month() === sunday.month()) {
        return `${monday.format('MMM D')}-${sunday.format('D')}`
      }
      return `${monday.format('MMM D')}-${sunday.format('MMM D')}`
    }

    // "MMM D-D" labels take ~50px each. Up to 5 weekly points fit comfortably
    // across the chart width; beyond that they overlap. Stride every other
    // label, anchored to first and last so the timeline bounds always read
    // (e.g. 10 points → indices 0, 2, 4, 6, 9). Stop two short of the end so
    // the stride pick can't crowd the forced last label.
    if (dates.length <= 5) {
      return dates.map(formatWeek)
    }

    const labels = new Array<string>(dates.length).fill('')
    labels[0] = formatWeek(dates[0])
    labels[dates.length - 1] = formatWeek(dates[dates.length - 1])
    for (let i = 2; i <= dates.length - 3; i += 2) {
      labels[i] = formatWeek(dates[i])
    }
    return labels
  }

  if (period === '3m' || period === '6m' || period === '1y') {
    const format = period === '1y' ? 'MM/YY' : "MMM 'YY"
    const monthGroups = new Map<string, number[]>()
    dates.forEach((date, i) => {
      const monthKey = dayjs(date).format('YYYY-MM')
      const group = monthGroups.get(monthKey)
      if (group) group.push(i)
      else monthGroups.set(monthKey, [i])
    })

    const monthKeys = Array.from(monthGroups.keys()).sort()
    // 1Y packs ~12 month labels into the same chart width as 3M/6M, which
    // crowds them and makes the axis unreadable. Stride every other month
    // (anchored to the last month so the most recent label always shows),
    // halving label count while still conveying the cadence.
    const stride = period === '1y' ? 2 : 1
    const labels = new Array<string>(dates.length).fill('')
    monthKeys.forEach((key, keyIndex) => {
      const indices = monthGroups.get(key) as number[]
      const isFirstMonth = keyIndex === 0
      const isLastMonth = keyIndex === monthKeys.length - 1
      // Skip months that fall between stride steps so 1Y shows alternating
      // months. Anchor the stride to the last month (which is always labeled
      // by the rule below) so the most recent label is preserved.
      const stepsFromLast = monthKeys.length - 1 - keyIndex
      if (!isLastMonth && stepsFromLast % stride !== 0) return
      // A partial first month (1-2 weekly points) would normally sit at
      // index 0 or 1 — right against the Y-axis — and clip the 0% tick.
      // Anchor its label to the month's LAST index instead so it shifts
      // right, away from the axis, while still falling inside its month
      // band on the timeline.
      if (isFirstMonth && !isLastMonth && indices.length < FULL_MONTH_THRESHOLD) {
        const anchorIndex = indices[indices.length - 1]
        labels[anchorIndex] = dayjs(dates[anchorIndex]).format(format)
        return
      }
      // Left-of-center midpoint. Math.floor(length / 2) lands on the 3rd
      // point of a 4-week month (~75% through) while a 5-week month sits at
      // its 3rd point (~50%), which reads as alternating 4- and 5-week gaps
      // across a 6M/1Y axis. Math.floor((length - 1) / 2) lands 4-week months
      // on their 2nd point (~33%) and keeps 5-week months centered, so
      // adjacent labels track the calendar cadence more evenly.
      const midIndex = indices[Math.floor((indices.length - 1) / 2)]
      labels[midIndex] = dayjs(dates[midIndex]).format(format)
    })
    return labels
  }

  return dates.map((date) => dayjs(date).format('MMM DD'))
}
